// User generic permissions
export const PERMISSIONS_KEY_MANAGE_VIEW = "ManageView";

// Node related permissions
export const PERMISSIONS_KEY_VIEW_NODE = "ViewNode";
export const PERMISSIONS_KEY_MANAGE_CONFIG = "ManageConfig";
export const PERMISSIONS_KEY_MANAGE_CACHE = "ManageCache";
export const PERMISSIONS_KEY_MANAGE_TEMPLATE = "ManageTemplate";
export const PERMISSIONS_KEY_NODE_DASHBOARD_SHARE = "NodeDashboardShare";
export const PERMISSIONS_KEY_IMPORT_DATA = "ImportData";
export const PERMISSIONS_KEY_APPROVE_PACKAGE = "ApprovePackage";

export const getNodePermissionStr = (permissionKey, nodeId) => `${permissionKey}_SingleNode_${nodeId}`;

// User state predicates
export const getIsUserAuthenticated = user => user && user.isAuthenticated;

export const getIsUserSuperadmin = user => getIsUserAuthenticated(user) && user.roles.includes("Administrator");

export const getHasNodePermission = (user, permissionKey) =>
  getIsUserAuthenticated(user) && user.permissions.some(p => p.startsWith(`${permissionKey}_SingleNode`));

// Permission checks
export const canDisplayAppSettingsForm = user => getIsUserSuperadmin(user);

export const canCreateNode = user => getIsUserSuperadmin(user);

export const canSortNodes = user => getIsUserSuperadmin(user);

export const canDisplayNodesSettingsForm = user =>
  getIsUserSuperadmin(user) ||
  getHasNodePermission(user, PERMISSIONS_KEY_MANAGE_CONFIG) ||
  getHasNodePermission(user, PERMISSIONS_KEY_MANAGE_CACHE) ||
  getHasNodePermission(user, PERMISSIONS_KEY_MANAGE_TEMPLATE) ||
  getHasNodePermission(user, PERMISSIONS_KEY_APPROVE_PACKAGE);

export const canDisplayUsersSettingsForm = user => getIsUserSuperadmin(user);

export const canDisplayCacheSettingForm = (user, nodeId) =>
  getIsUserSuperadmin(user) ||
  (getIsUserAuthenticated(user) &&
    user.permissions.includes(getNodePermissionStr(PERMISSIONS_KEY_MANAGE_CACHE, nodeId)));

export const canDisplayTemplatesSettingsForm = (user, nodeId) =>
  getIsUserSuperadmin(user) ||
  (getIsUserAuthenticated(user) &&
    user.permissions.includes(getNodePermissionStr(PERMISSIONS_KEY_MANAGE_TEMPLATE, nodeId)));

export const canSaveTemplate = (user, nodeId) =>
  getIsUserSuperadmin(user) ||
  (getIsUserAuthenticated(user) &&
    user.permissions.includes(getNodePermissionStr(PERMISSIONS_KEY_MANAGE_TEMPLATE, nodeId)));

export const canEditNode = (user, nodeId) =>
  getIsUserSuperadmin(user) ||
  (getIsUserAuthenticated(user) &&
    user.permissions.includes(getNodePermissionStr(PERMISSIONS_KEY_MANAGE_CONFIG, nodeId)));

export const canViewNode = (user, nodeId) =>
  getIsUserSuperadmin(user) ||
  (getIsUserAuthenticated(user) && user.permissions.includes(getNodePermissionStr(PERMISSIONS_KEY_VIEW_NODE, nodeId)));

export const canDisplayUserViews = user => getIsUserAuthenticated(user);

export const canSaveAsView = user => getIsUserAuthenticated(user);

export const canClearServerCache = user => getIsUserSuperadmin(user);

export const canGetQueryLog = user => getIsUserSuperadmin(user);

export const canDisplayPermissionsSettingsForm = user => getIsUserSuperadmin(user);

export const canManagePersonalCustomPages = user => getIsUserSuperadmin(user);

export const canManageCustomPages = user => getIsUserSuperadmin(user);

export const canManageFilters = user => getIsUserSuperadmin(user);

export const canManageNodeCustomPages = (user, nodeId) =>
  getIsUserSuperadmin(user) ||
  (getIsUserAuthenticated(user) &&
    user.permissions.includes(getNodePermissionStr(PERMISSIONS_KEY_MANAGE_CONFIG, nodeId)));

export const canManagePersonalDashboards = user =>
  getIsUserSuperadmin(user) ||
  getHasNodePermission(user, PERMISSIONS_KEY_MANAGE_CONFIG) ||
  getHasNodePermission(user, PERMISSIONS_KEY_NODE_DASHBOARD_SHARE);

export const canManageAppDashboards = user => getIsUserSuperadmin(user);

export const canManageAppCustomPages = user => getIsUserSuperadmin(user);

export const canManageNodeDashboards = (user, nodeId) =>
  getIsUserSuperadmin(user) ||
  (getIsUserAuthenticated(user) &&
    user.permissions.includes(getNodePermissionStr(PERMISSIONS_KEY_MANAGE_CONFIG, nodeId)));

export const canDeleteNodes = user => getIsUserSuperadmin(user);

export const canViewTimesLog = (user, nodeId) =>
  getIsUserSuperadmin(user) ||
  (getIsUserAuthenticated(user) &&
    user.permissions.includes(getNodePermissionStr(PERMISSIONS_KEY_MANAGE_CONFIG, nodeId)));

export const canManageCache = (user, nodeId) =>
  getIsUserSuperadmin(user) ||
  (getIsUserAuthenticated(user) &&
    user.permissions.includes(getNodePermissionStr(PERMISSIONS_KEY_MANAGE_TEMPLATE, nodeId)));

export const canViewTemplateOrAnnotationIcon = (user, nodeId) =>
  getIsUserSuperadmin(user) ||
  (getIsUserAuthenticated(user) &&
    user.permissions.includes(getNodePermissionStr(PERMISSIONS_KEY_MANAGE_TEMPLATE, nodeId)));

export const canUseDatasetTools = user => getIsUserAuthenticated(user);

export const canSetMachineToMachinePassword = user => getIsUserSuperadmin(user);

export const canImportData = (user, nodeId) =>
  getIsUserSuperadmin(user) || user.permissions.includes(getNodePermissionStr(PERMISSIONS_KEY_IMPORT_DATA, nodeId));

export const canApprovePackage = (user, nodeId) =>
  getIsUserSuperadmin(user) || user.permissions.includes(getNodePermissionStr(PERMISSIONS_KEY_APPROVE_PACKAGE, nodeId));
